<template>
    <transition>
        <div class="container-fluid page-content">
            <!-- Modern Page Header -->
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">User List</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">User Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">User List</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal()" v-if="permission['user-create']">
                        <i class="fas fa-plus"></i> Create New
                    </button>
                </div>
            </div>

            <!-- Modern Filter Dashboard Bar -->
            <div class="card border-0 shadow-sm filter-card mb-4" v-if="items.length > 0 || tableData.search || tableData.company_id || tableData.outlet_id">
                <div class="card-body">
                    <div class="row align-items-center justify-content-between g-3">
                        <div class="col-12 col-md-auto d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select form-select-custom length-select" v-model="tableData.length" @change="fetchItems()">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option :value="pagination.total">All</option>
                            </select>
                            <span class="text-muted text-nowrap">Entries</span>
                        </div>

                        <div class="col-12 col-md-auto d-flex flex-wrap align-items-center gap-3 ms-md-auto">
                            <!-- Company Filter -->
                            <div class="filter-select-wrapper" style="min-width: 180px;">
                                <select class="form-select form-select-custom" v-model="tableData.company_id" @change="fetchItems()">
                                    <option value="">All Companies</option>
                                    <option v-for="company in companies" :key="company.id" :value="company.id">
                                        {{ company.name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Outlet Filter -->
                            <div class="filter-select-wrapper" style="min-width: 180px;">
                                <select class="form-select form-select-custom" v-model="tableData.outlet_id" @change="fetchItems()">
                                    <option value="">All Outlets</option>
                                    <option v-for="outlet in outlet_data" :key="outlet.id" :value="outlet.id">
                                        {{ outlet.name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Search Input -->
                            <div class="position-relative search-container">
                                <i class="fas fa-search search-icon text-muted"></i>
                                <input type="text" class="form-control form-control-custom search-input" v-model="tableData.search" placeholder="Search..." @input="fetchItems()">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modern Table List Card -->
            <div class="card border-0 shadow-sm table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-premium mb-0" v-if="!loading && items.length > 0">
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 5%;">ID</th>
                                    <th class="text-center" style="width: 8%;">Image</th>
                                    <th class="text-start" style="width: 12%;">Name</th>
                                    <th class="text-start" style="width: 15%;">Email</th>
                                    <th class="text-start" style="width: 10%;">User Code</th>
                                    <th class="text-start" style="width: 10%;">Phone</th>
                                    <th class="text-start" style="width: 12%;">Company</th>
                                    <th class="text-start" style="width: 12%;">Outlet</th>
                                    <th class="text-start" style="width: 8%;">Role</th>
                                    <th class="text-center" style="width: 8%;">Status</th>
                                    <th class="text-end" style="width: 50px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in items" :key="i">
                                    <td class="text-center text-muted fw-semibold">{{ item.id }}</td>
                                    <td class="text-center">
                                        <div class="image-avatar-container d-inline-block">
                                            <img :src="item.profile_image" class="image-avatar" v-if="item.profile_image" alt="Profile Image">
                                            <div class="image-avatar-placeholder" v-else>
                                                <i class="fas fa-user text-muted"></i>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-start">
                                        <span class="fw-bold text-slate-800">{{ item.name }}</span>
                                    </td>
                                    <td class="text-start text-muted" style="word-break: break-all;">{{ item.email }}</td>
                                    <td class="text-start"><code>{{ item.user_code }}</code></td>
                                    <td class="text-start text-muted">{{ item.phone }}</td>
                                    <td class="text-start text-muted">{{ item.company_name }}</td>
                                    <td class="text-start text-muted">{{ item.outlet_name || '—' }}</td>
                                    <td class="text-start">
                                        <span class="badge-role">{{ item.roles && item.roles[0] ? item.roles[0].name : '—' }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span v-if="item.status==1" class="badge-role badge-role-active">Active</span>
                                        <span v-if="item.status==0" class="badge-role badge-role-inactive">Inactive</span>
                                    </td>
                                    <td class="text-end">
                                        <div class="dropdown d-inline-block">
                                            <button class="btn btn-action-dropdown dropdown-toggle arrow-none" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">
                                                <li v-if="permission['user-outlet-assign'] || (item.roles && item.roles.some(data => data.name == 'Sales Man' || data.name == 'Shop Manager'))">
                                                    <a href="javascript:void(0);" class="dropdown-item text-primary" @click="outletAssign(item)">
                                                        <i class="fas fa-store me-2"></i> Outlet Assign
                                                    </a>
                                                </li>
                                                <li v-if="permission['user-edit']">
                                                    <a href="javascript:void(0);" class="dropdown-item text-warning" @click="edit(item)">
                                                        <i class="fas fa-edit me-2"></i> Edit
                                                    </a>
                                                </li>
                                                <li v-if="permission['user-delete']">
                                                    <a href="javascript:void(0);" class="dropdown-item text-danger" @click="deleteItem(item)">
                                                        <i class="fas fa-trash-alt me-2"></i> Remove
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Fallback State -->
                        <div v-if="!loading && items.length === 0" class="text-center py-5">
                            <div class="empty-state-icon mb-3">
                                <i class="fas fa-users-cog fa-3x text-muted"></i>
                            </div>
                            <h5 class="text-slate-700 fw-bold">No Users Found</h5>
                            <p class="text-muted mb-0 small">Try adjusting your filters or search keywords.</p>
                        </div>

                        <!-- Loading State -->
                        <div class="text-center py-5" v-if="loading">
                            <div class="spinner-premium mb-3 mx-auto"></div>
                            <h5 class="text-indigo fw-semibold">Loading Users...</h5>
                        </div>
                    </div>
                </div>

                <!-- Table Footer with Pagination -->
                <div class="card-footer bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3" v-if="!loading && items.length > 0">
                    <div class="text-muted small">
                        Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
                    </div>
                    <Pagination :pagination="pagination" :language="lang" @onChangePage="setPage"></Pagination>
                </div>
            </div>

            <!-- Modal User Add / Edit -->
            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium modal-premium-md">
                    <div class="modal-header border-0 pb-0">
                        <h4 class="fw-bold m-0 text-slate-800">User Add Or Edit</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <div class="row g-4">
                                <div class="col-md-8">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Name *</label>
                                            <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.name}" @keypress="onkeyPress('name')" v-model="form.name" id="name" placeholder="User Name" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{ errors.name[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="email" class="form-label-custom text-slate-700 fw-semibold mb-2">Email *</label>
                                            <input type="email" class="form-control form-control-custom" :class="{'is-invalid': errors.email}" @keypress="onkeyPress('email')" v-model="form.email" id="email" placeholder="User Email" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.email">
                                                {{ errors.email[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="user_code" class="form-label-custom text-slate-700 fw-semibold mb-2">User Code *</label>
                                            <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.user_code}" @keypress="onkeyPress('user_code')" v-model="form.user_code" id="user_code" placeholder="User Code" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.user_code">
                                                {{ errors.user_code[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="phone" class="form-label-custom text-slate-700 fw-semibold mb-2">Phone *</label>
                                            <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.phone}" @keypress="onkeyPress('phone')" v-model="form.phone" id="phone" placeholder="Contact Number">
                                            <div class="invalid-feedback" v-if="errors.phone">
                                                {{ errors.phone[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company *</label>
                                            <select class="form-select form-select-custom" :class="{'is-invalid': errors.company_id}" v-model="form.company_id" @change="onkeyPress('company_id')" id="company_id">
                                                <option value="">Select company</option>
                                                <option v-for="(company, index) in companies" :value="company.id" :key="index">
                                                    {{ company.name }}
                                                </option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.company_id">
                                                {{ errors.company_id[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="roles" class="form-label-custom text-slate-700 fw-semibold mb-2">Role</label>
                                            <select class="form-select form-select-custom" :class="{'is-invalid': errors.role_id}" v-model="form.roles" @change="onkeyPress('roles')" id="roles">
                                                <option value="">Select Role</option>
                                                <option v-for="(role, index) in roles" :value="index" :key="index">
                                                    {{ role }}
                                                </option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.role_id">
                                                {{ errors.role_id[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="password" class="form-label-custom text-slate-700 fw-semibold mb-2">{{ isRequired ? 'Password *' : 'Password' }}</label>
                                            <input type="password" class="form-control form-control-custom" :class="{'is-invalid': errors.password}" @keypress="onkeyPress('password')" v-model="form.password" id="password" placeholder="Password">
                                            <div class="invalid-feedback" v-if="errors.password">
                                                {{ errors.password[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="confirm_password" class="form-label-custom text-slate-700 fw-semibold mb-2">{{ isRequired ? 'Confirm Password *' : 'Confirm Password' }}</label>
                                            <input type="password" class="form-control form-control-custom" :class="{'is-invalid': errors.password_confirmation}" @keypress="onkeyPress('password_confirmation')" v-model="form.password_confirmation" id="confirm_password" placeholder="Confirm Password">
                                            <div class="invalid-feedback" v-if="errors.password_confirmation">
                                                {{ errors.password_confirmation[0] }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label for="dstatus" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                            <select class="form-select form-select-custom" :class="{'is-invalid': errors.status}" v-model="form.status" @change="onkeyPress('status')" id="dstatus">
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.status">
                                                {{ errors.status[0] }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-4 d-flex flex-column align-items-center">
                                    <label class="form-label-custom text-slate-700 fw-semibold mb-2 align-self-start">Profile Image</label>
                                    <div class="modal-image-preview-container mb-3" style="width: 180px; height: 180px;">
                                        <img :src="imagePreview" class="modal-image-preview" v-if="imagePreview" alt="Profile Preview">
                                        <img :src="form.profile_image" class="modal-image-preview" v-else-if="editMode && form.profile_image" alt="Profile Current">
                                        <div class="modal-image-placeholder text-center" v-else>
                                            <i class="fas fa-camera text-slate-300 fa-2x mb-2"></i>
                                            <span class="small text-muted">No Image</span>
                                        </div>
                                    </div>
                                    <div class="file-upload-wrapper w-100 text-center">
                                        <span class="btn-outline-file-upload w-100 justify-content-center">
                                            <i class="fas fa-camera me-2"></i> Choose Image
                                        </span>
                                        <input type="file" class="file-upload-input" name="profile_image" @change="onImageChange" />
                                    </div>
                                    <div class="invalid-feedback text-center mt-2 d-block" v-if="errors.profile_image">
                                        {{ errors.profile_image[0] }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0 justify-content-end gap-2">
                            <button type="button" class="btn btn-outline-premium px-4" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium px-4" :disabled="disabled">
                                <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                {{ btn }} User
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>

            <!-- Modal User Outlet Assign -->
            <Modal @close="toggleAssignModal()" :modalActive="assignModalActive">
                <div class="modal-content border-0 shadow-lg modal-premium outlet-assign">
                    <div class="modal-header border-0 pb-0">
                        <h4 class="fw-bold m-0 text-slate-800">Outlet Assignment</h4>
                        <button @click="toggleAssignModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitAssignForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <input type="hidden" v-model="assignform.user_id">
                            <div class="mb-0">
                                <label for="outlets-select" class="form-label-custom text-slate-700 fw-semibold mb-2">Select Outlets *</label>
                                <Multiselect id="outlets-select" mode="tags" v-model="assignform.outlets" placeholder="Select Outlet" :classes="multiclasses" :searchable="true" :options="outlets" :close-on-select="true" :create-option="true" @change="onkeyPress('outlets')" />
                                <div class="invalid-feedback mt-2 d-block" v-if="errors.outlets">
                                    {{ errors.outlets[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0 justify-content-end gap-2">
                            <button type="button" class="btn btn-outline-premium px-4" @click="toggleAssignModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium px-4" :disabled="assignDisabled">
                                <span v-show="isAssignSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                Assign Outlets
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    </transition>
</template>

<script>
import Modal from "./../helper/Modal";
import Form from 'vform'
import axios from 'axios';
import Pagination from '@/components/Pagination.vue';

export default {
   name: 'User',
   components: {
      Modal,
      Pagination
   },
   props: {
      language: {
         type: Object,
         default: () => {
            return {
               lengthMenu: null,
               info: null,
               zeroRecords: null,
               search: null
            }
         },
      },
   },
   data() {
      return {
         base_url: this.apiUrl,
         loading: true,
         isSubmit: false,
         isRequired: true,
         showModal: false,
         editMode: false,
         disabled: false,
         assignDisabled: false,
         modalActive: false,
         assignModalActive: false,
         isAssignSubmit: false,
         errors: {},
         imagePreview: '',
         btn: 'Create',
         items: [],
         companies: [],
         roles: [],
         outlet_data: [],
         outlets: [],
         form: new Form({
            id: '',
            name: '',
            email: '',
            user_code: '',
            phone: '',
            password: '',
            password_confirmation: '',
            profile_image: '',
            company_id: '',
            roles: [],
            status: 1
         }),
         assignform: new Form({
            user_id: '',
            outlets: [],
         }),
         multiclasses: {
            clear: '',
            clearIcon: '',
         },
         tableData: {
            draw: 0,
            length: 10,
            search: '',
            column: 0,
            dir: 'desc',
            sortKey: 'id',
            company_id: '',
            outlet_id: '',
         },
         lang: {
            lengthMenu: this.$props.language.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
            info: this.$props.language.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
            zeroRecords: this.$props.language.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.',
            search: this.$props.language.search ? this.$props.language.search : 'Search'
         },
         pagination: {
            lastPage: '',
            currentPage: '',
            total: '',
            lastPageUrl: '',
            nextPageUrl: '',
            prevPageUrl: '',
            from: '',
            to: '',
            links: [],
         },
      };
   },
   methods: {
      toggleModal: function () {
         this.modalActive = !this.modalActive;
         if (!this.modalActive) {
            this.editMode = false;
            this.isRequired = true;
            this.btn = 'Create';
         }
         this.errors = '';
         this.isSubmit = false;
         this.form.reset();
      },
      toggleAssignModal: function () {
         this.assignModalActive = !this.assignModalActive;
         if (!this.assignModalActive) {
            this.btn = 'Assign';
            this.assignform.reset();
         }
         this.errors = '';
         this.isAssignSubmit = false;
      },
      edit: function (item) {
         this.btn = 'Update';
         this.editMode = true;
         this.isRequired = false;
         this.toggleModal();
         this.form.fill(item);
         this.form.roles = item.roles && item.roles[0] ? item.roles[0].id : '';
         this.imagePreview = '';
      },

      submitForm: function () {
         this.isSubmit = true;
         this.disabled = true;
         const formData = new FormData();
         formData.append('name', this.form.name);
         formData.append('company_id', this.form.company_id);
         formData.append('email', this.form.email);
         formData.append('user_code', this.form.user_code);
         formData.append('phone', this.form.phone);
         formData.append('password', this.form.password || '');
         formData.append('password_confirmation', this.form.password_confirmation || '');
         formData.append('roles', this.form.roles || '');
         formData.append('status', this.form.status);
         if (this.editMode) {
            formData.append('_method', 'put');
            if (this.imagePreview) {
               this.form.profile_image ? formData.append('profile_image', this.form.profile_image, this.form.profile_image.name) : '';
            }
            var postEvent = axios.post(this.base_url + '/users/' + this.form.id, formData, this.headers);
         } else {
            this.form.profile_image ? formData.append('profile_image', this.form.profile_image, this.form.profile_image.name) : '';
            var postEvent = axios.post(this.base_url + '/users', formData, this.headers);
         }
         postEvent.then(res => {
            this.isSubmit = false;
            this.disabled = false;
            if (res.status == 200) {
               this.toggleModal();
               this.fetchItems();
               this.$toast.success(res.data.message);
            } else {
               this.$toast.error(res.data.message);
            }
         }).catch(err => {
            this.isSubmit = false;
            this.disabled = false;
            this.$toast.error(err.response.data.message);
            if (err.response.status == 422) {
               this.errors = err.response.data.errors
            }
         });
      },

      outletAssign: function (item) { 
         this.assignform.user_id = item.id;
         this.outlets = this.outlet_data.map(({
            id,
            name
         }) => ({
            label: name,
            value: id
         }));

         this.toggleAssignModal();
      },

      submitAssignForm: function () {
         this.isAssignSubmit = true;
         this.assignDisabled = true;
         const formData = new FormData();
         formData.append('user_id', this.assignform.user_id);
         formData.append('outlets', this.assignform.outlets);

         var postEvent = axios.post(this.base_url + '/users/outlet_assign', formData, this.headers);

         postEvent.then(res => {
            this.isAssignSubmit = false;
            this.assignDisabled = false;
            if (res.status == 200) {
               this.toggleAssignModal();
               this.fetchItems();
               this.$toast.success(res.data.message);
            } else {
               this.$toast.error(res.data.message);
            }
         }).catch(err => {
            this.isAssignSubmit = false;
            this.assignDisabled = false;
            this.$toast.error(err.response.data.message);
            if (err.response.status == 422) {
               this.errors = err.response.data.errors
            }
         });
      },

      onImageChange(e) {
         this.form.profile_image = e.target.files[0]
         let files = e.target.files || e.dataTransfer.files;
         if (!files.length)
            return;

         this.createImage(files[0]);
      },
      createImage(file) {
         let reader = new FileReader();
         let vm = this;
         reader.onload = (e) => {
            vm.imagePreview = e.target.result;
         };
         reader.readAsDataURL(file);
      },
      validation: function (...fiels) {
         var obj = new Object();
         for (var k in fiels) {
            for (var j in this.form) {
               if ((j == fiels[k]) && (!this.form[j])) {
                  obj[fiels[k]] = fiels[k].replace("_", " ") + ' field is required';
                  this.errors = {
                     ...this.errors,
                     ...obj
                  };
               }
            }
         }
      },
      onkeyPress: function (field) {
         for (var k in this.errors) {
            if (k == field) {
               delete this.errors[k];
            }
         }
      },
      deleteItem: function (item) {
         this.$swal({
            title: 'Are you sure?',
            text: "You want delete this item!",
            showCancelButton: true,
            confirmButtonCategory: '#3085d6',
            cancelButtonCategory: '#d33',
            confirmButtonText: 'Yes, delete it!'
         }).then((result) => {
            if (result.value) {
               axios.delete(this.base_url + '/users/' + item.id, this.headers).then(res => {
                  if (res.status == 200) {
                     this.fetchItems();
                     this.$toast.success(res.data.message);
                  } else {
                     this.$toast.error(res.data.message);
                  }
               }).catch(err => {
                  this.$toast.error(err.response.data.message);
               })
            }
         });
      },

      fetchHelperData() {
         axios.get(this.apiUrl + '/user-helper-data', this.headerjson)
            .then((response) => {
               this.roles = response.data.data.roles;
               this.outlet_data = response.data.data.outlet;
               this.companies = response.data.data.companies;
            })
            .catch(errors => {
               console.log(errors);
            })
            .finally(() => {
               this.loading = false;
            });
      },
      fetchItems(url = this.apiUrl + '/user-list') {
         this.tableData.draw++;
         axios.get(url, {
               params: this.tableData,
               headers: this.headerparams
            })
            .then((response) => {
               let data = response.data.data;
               if (this.tableData.draw = data.draw) {
                  this.items = data.data.data;
                  this.configPagination(data.data);
               }
            })
            .catch(errors => {
               console.log(errors);
            })
            .finally(() => {
               this.loading = false;
            });
      },

      configPagination(data) {
         this.pagination.lastPage = data.last_page;
         this.pagination.currentPage = data.current_page;
         this.pagination.total = data.total ? data.total : 0;
         this.pagination.lastPageUrl = data.last_page_url;
         this.pagination.nextPageUrl = data.next_page_url;
         this.pagination.prevPageUrl = data.prev_page_url;
         this.pagination.from = data.from ? data.from : 0;
         this.pagination.to = data.to ? data.to : 0;
         this.pagination.links = data.links;
      },

      sortBy(key, sortable) {
         this.tableData.sortKey = key;
         this.tableData.column = this.getIndex(this.columns, 'name', key);
         this.tableData.dir = sortable;
         this.fetchItems();
      },
      setPage(data) {
         this.fetchItems(data.url);
      },
      getIndex(array, key, value) {
         return array.findIndex(i => i[key] == value)
      }

   },
   created() {
      this.fetchHelperData();
      this.fetchItems();
   },
   destroyed() {},
   mounted() {
      window.scrollTo(0, 0);
   },
   computed: {
      permission() {
         let pname = this.$route.meta.parent_module;
         let module_name = this.$route.meta.module_name;
         let path_name = this.$route.path; 
         let data = '';
         if(this.$route.meta.parent_module){
            data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
         }else{
            data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
         } 
         return data;
      },
   }
}
</script>